import { useAppSelector } from 'app/hooks';
import {
  ductedKeptComponentIds,
  resultSetsFor
} from 'features/ducted-compatibility';
import { useMemo } from 'react';
import { getChooseSystemsEligibleDuctedSystems } from 'utils/existing-equipment';
import {
  describeRegulationLocation,
  distinctAppliedRegulations,
  joinRegulationLabels
} from 'utils/location-regulation-labels';

// Location-regulations banner for Choose Systems: tells the user that the
// server dropped every system the cart would flag for the project's location
// (ahapdb ProductRegulations — Low NOx / Ultra-Low NOx furnaces, ...) and
// which rules did it. Informational only: the regulations are law, so unlike
// SizingBandsBanner there is no relax toggle. Renders nothing when no
// in-play result set reports the rules excluded anything — no ZIP, no
// applicable regulation, or every candidate already compliant. Error-settled
// entries are carved out, matching the sizing banner: a failed refetch
// retains stale data under status 'error', and the banner must not claim
// compliance over rows the server never vetted for this request.
export default function LocationRegulationsBanner() {
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );
  const recommendationSelectionsBySystem = useAppSelector(
    (state) => state.structure.recommendationSelectionsBySystem
  );
  const wantCompositionPicksBySystem = useAppSelector(
    (state) => state.structure.wantCompositionPicksBySystem
  );
  const resultsBySystem = useAppSelector(
    (state) => state.ductedCompatibility.resultsBySystem
  );
  const locationData = useAppSelector((state) => state.project.locationData);

  // One pass over the (eligible system x in-play result set) entries — kept
  // OR alternatives — collecting the regulations the server reports as
  // having excluded rows (zero-results sets count too: pnet echoes true
  // whenever the rules excluded rows, including down to zero).
  const appliedRegulations = useMemo(() => {
    const collected = getChooseSystemsEligibleDuctedSystems(
      existingSystems,
      recommendationSelectionsBySystem,
      wantCompositionPicksBySystem
    ).flatMap((system) =>
      resultSetsFor(ductedKeptComponentIds(system)).flatMap((resultSet) => {
        const setEntry = resultsBySystem[system.uniqueId]?.[resultSet];
        return setEntry?.regulationsApplied === true &&
          setEntry.status !== 'error'
          ? setEntry.appliedRegulations
          : [];
      })
    );
    return distinctAppliedRegulations(collected);
  }, [
    existingSystems,
    recommendationSelectionsBySystem,
    wantCompositionPicksBySystem,
    resultsBySystem
  ]);

  if (appliedRegulations.length === 0) {
    return null;
  }

  const requirement =
    appliedRegulations.length === 1 ? 'requirement' : 'requirements';

  return (
    <div
      data-testid='location-regulations-banner'
      className='flex flex-wrap items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2'
    >
      <span className='text-xs text-gray-600'>
        Showing only systems that meet the{' '}
        {joinRegulationLabels(appliedRegulations)} {requirement} for{' '}
        {describeRegulationLocation(locationData)}.
      </span>
    </div>
  );
}
